import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  hasAnsweredCookieConsent,
  openCookieSettings,
  OPEN_COOKIE_SETTINGS_EVENT,
  readCookieConsent,
  saveCookieConsent,
} from '../utils/cookieConsent';
import './CookieConsentBanner.css';

function PreferencesPanel({ draft, setDraft, onSave, onCancel, t }) {
  return (
    <div className="cookie-consent__prefs" role="group" aria-label={t('cookieConsent.prefsAria')}>
      <label className="cookie-consent__row">
        <span>
          <strong>{t('cookieConsent.necessaryTitle')}</strong>
          <span className="cookie-consent__hint">{t('cookieConsent.necessaryHint')}</span>
        </span>
        <input type="checkbox" checked disabled aria-disabled="true" />
      </label>
      <label className="cookie-consent__row">
        <span>
          <strong>{t('cookieConsent.analyticsTitle')}</strong>
          <span className="cookie-consent__hint">{t('cookieConsent.analyticsHint')}</span>
        </span>
        <input
          type="checkbox"
          checked={draft.analytics}
          onChange={(e) => setDraft((d) => ({ ...d, analytics: e.target.checked }))}
        />
      </label>
      <label className="cookie-consent__row">
        <span>
          <strong>{t('cookieConsent.marketingTitle')}</strong>
          <span className="cookie-consent__hint">{t('cookieConsent.marketingHint')}</span>
        </span>
        <input
          type="checkbox"
          checked={draft.marketing}
          onChange={(e) => setDraft((d) => ({ ...d, marketing: e.target.checked }))}
        />
      </label>
      <div className="cookie-consent__actions cookie-consent__actions--prefs">
        <button type="button" className="cookie-consent__btn cookie-consent__btn--ghost" onClick={onCancel}>
          {t('common.cancel', 'Cancel')}
        </button>
        <button type="button" className="cookie-consent__btn cookie-consent__btn--primary" onClick={onSave}>
          {t('cookieConsent.savePrefs')}
        </button>
      </div>
    </div>
  );
}

/**
 * GDPR / ePrivacy cookie banner with Google Consent Mode updates.
 */
export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => !hasAnsweredCookieConsent());
  const [showPrefs, setShowPrefs] = useState(false);
  const [draft, setDraft] = useState(() => {
    const existing = readCookieConsent();
    return {
      analytics: Boolean(existing?.analytics),
      marketing: Boolean(existing?.marketing),
    };
  });

  useEffect(() => {
    const onOpen = () => {
      const existing = readCookieConsent();
      setDraft({
        analytics: Boolean(existing?.analytics),
        marketing: Boolean(existing?.marketing),
      });
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
  }, []);

  const close = () => {
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => {
    saveCookieConsent({ analytics: true, marketing: true });
    close();
  };

  const rejectNonEssential = () => {
    saveCookieConsent({ analytics: false, marketing: false });
    close();
  };

  const savePrefs = () => {
    saveCookieConsent(draft);
    close();
  };

  if (!visible) {
    return (
      <button
        type="button"
        className="cookie-consent__reopen"
        onClick={() => openCookieSettings()}
        aria-label={t('cookieConsent.reopenAria')}
      >
        {t('cookieConsent.cookieSettings')}
      </button>
    );
  }

  return (
    <div className="cookie-consent" role="dialog" aria-modal="true" aria-labelledby="cookie-consent-title">
      <div className="cookie-consent__panel">
        <h2 id="cookie-consent-title" className="cookie-consent__title">
          {t('cookieConsent.title')}
        </h2>
        <p className="cookie-consent__body">
          {t('cookieConsent.body')}{' '}
          <Link to="/cookies" className="cookie-consent__link">
            {t('cookieConsent.cookiePolicy')}
          </Link>
          {' · '}
          <Link to="/privacy" className="cookie-consent__link">
            {t('cookieConsent.privacyPolicy')}
          </Link>
          {' · '}
          <Link to="/ai-transparency" className="cookie-consent__link">
            {t('cookieConsent.aiTransparency')}
          </Link>
        </p>

        {showPrefs ? (
          <PreferencesPanel
            draft={draft}
            setDraft={setDraft}
            onSave={savePrefs}
            onCancel={() => setShowPrefs(false)}
            t={t}
          />
        ) : (
          <div className="cookie-consent__actions">
            <button type="button" className="cookie-consent__btn cookie-consent__btn--ghost" onClick={rejectNonEssential}>
              {t('cookieConsent.reject')}
            </button>
            <button type="button" className="cookie-consent__btn cookie-consent__btn--secondary" onClick={() => setShowPrefs(true)}>
              {t('cookieConsent.customize')}
            </button>
            <button type="button" className="cookie-consent__btn cookie-consent__btn--primary" onClick={acceptAll}>
              {t('cookieConsent.acceptAll')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
